/**
 * River Visual 3D Engine - Main App Component
 * Production-ready 3D model viewer untuk VPS deployment
 */

import React, { useEffect, useState } from 'react'
import ModelViewer from '../ModelViewer' // NEW refactored version!

function App() {
  const [threeLoaded, setThreeLoaded] = useState(false)
  const [loadError, setLoadError] = useState(null)

  // Load Three.js from local npm package (OFFLINE - NO INTERNET NEEDED!)
  useEffect(() => {
    const loadThreeJS = async () => {
      try {
        console.log('Loading Three.js modules from local package...')

        // Import Three.js dari npm package (offline)
        const THREE = await import('three')
        const { OrbitControls } = await import('three/examples/jsm/controls/OrbitControls.js')
        const { TransformControls } = await import('three/examples/jsm/controls/TransformControls.js')
        const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js')
        const { RGBELoader } = await import('three/examples/jsm/loaders/RGBELoader.js')
        const { EXRLoader } = await import('three/examples/jsm/loaders/EXRLoader.js')

        // Expose to window (ModelViewer.jsx menggunakan window.THREE_MODULE)
        window.THREE_MODULE = THREE
        window.THREE = THREE
        window.OrbitControls = OrbitControls
        window.TransformControls = TransformControls
        window.GLTFLoader = GLTFLoader
        window.RGBELoader = RGBELoader
        window.EXRLoader = EXRLoader

        console.log('✅ Three.js loaded successfully (OFFLINE MODE)')
        setThreeLoaded(true)
      } catch (err) {
        console.error('Failed to load Three.js:', err)
        setLoadError('Failed to load 3D engine. Please refresh the page.')
      }
    }

    loadThreeJS()
  }, [])

  // Display loading screen
  if (!threeLoaded) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a1a1f 0%, #2d2d35 100%)',
        color: 'white',
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid rgba(255, 255, 255, 0.1)',
          borderTopColor: '#00d4ff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
        <p style={{ marginTop: '20px', fontSize: '16px' }}>
          {loadError || 'Loading River Visual 3D Engine...'}
        </p>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    )
  }

  // Display error screen
  if (loadError) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a1a1f 0%, #2d2d35 100%)',
        color: 'white',
        padding: '20px',
        textAlign: 'center',
      }}>
        <h1 style={{ fontSize: '24px', marginBottom: '10px', color: '#ef4444' }}>
          ⚠️ Error
        </h1>
        <p style={{ fontSize: '16px', marginBottom: '20px' }}>
          {loadError}
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '10px 20px',
            fontSize: '14px',
            background: '#00d4ff',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          Reload Page
        </button>
      </div>
    )
  }

  return (
    <div style={{ width: '100vw', height: '100vh', overflow: 'hidden' }}>
      <ModelViewer 
        // ========================================
        // 🎯 LOAD MODEL DARI PUBLIC FOLDER
        // ========================================
        
        // CARA 1: Download test model dulu (recommended untuk testing)
        // Run: download-test-model.bat
        // Kemudian uncomment line di bawah:
        modelUrl="/models/003%20Hoodie.glb"  // %20 = spasi (URL encoded)
        
        // CARA 2: Gunakan model Anda sendiri
        // 1. Copy file GLB/GLTF Anda ke: public/models/
        // 2. Uncomment dan ganti nama file:
        // modelUrl="/models/nama-model-anda.glb"
        
        // CARA 3: Test langsung dengan external URL
        // modelUrl="https://threejs.org/examples/models/gltf/DamagedHelmet/glTF/DamagedHelmet.gltf"
        
        // ⚠️ PENTING: 
        // - Path harus diawali dengan / (slash)
        // - Tidak perlu tulis "public" di path
        // - Contoh benar: "/models/robot.glb"
        // - Contoh salah: "public/models/robot.glb"
        // ========================================
        
        // Scene settings
        backgroundColor="#1a1a1f"
        showPanel={true}
        showCameraHelpers={true}
        showFloorGrid={true}
        showObjectExtras={true}
        
        // Lighting
        lightIntensity={1}
        ambientLightIntensity={0.5}
        showDirectionalLights={true}
        
        // Shadows
        enableShadows={true}
        shadowIntensity={0.3}
        showGroundPlane={true}
        
        // Advanced
        enableAutoSmooth={true}
        autoSmoothAngle={30}
        
        // UI Panels
        showLightsPanel={true}
        showCameraPanel={true}
        
        // Camera
        cameraFov={50}
        cameraNear={0.01}
        cameraFar={1000}
        autoRotate={false}
      />
    </div>
  )
}

export default App

